import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtNum, fmtInt, networkLabel } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Link } from "react-router-dom";
import { ArrowUpDown, ArrowUp, ArrowDown, Search, ArrowUpRight, ArrowDownRight, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import CopyButton from "./CopyButton";
import BudgetQuickPicker from "./BudgetQuickPicker";
import RecentBudgetBadge from "./RecentBudgetBadge";

const COLUMNS = [
  { key: "adset_name", label: "Ad set", align: "left" },
  { key: "campaign_name", label: "Campaign", align: "left" },
  { key: "status", label: "Status", align: "left" },
  { key: "daily_budget", label: "Daily Budget", align: "right", money: true },
  { key: "spend", label: "Spend", align: "right", money: true },
  { key: "ctr", label: "CTR %", align: "right", pct: true },
  { key: "platform_registrations", label: "Plat. Reg.", align: "right", num: 1 },
  { key: "platform_cpa", label: "Plat. CPA", align: "right", money: true, colorize: true },
  { key: "tm_registrations", label: "TM Reg.", align: "right", num: 1 },
  { key: "tm_cpa", label: "TM CPA", align: "right", money: true, colorize: true },
  { key: "variance_pct", label: "Var. %", align: "right", variance: true },
  { key: "actions", label: "Quick", align: "right", sortable: false },
];

export default function AdsetView({ accounts, network, accountId, accountIds, days, startDate, endDate, minSpend, search }) {
  const [adsets, setAdsets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState("spend");
  const [sortDir, setSortDir] = useState("desc");
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("");

  const params = useMemo(() => {
    const p = { min_spend: minSpend || 0 };
    if (days === "custom") {
      if (startDate) p.start_date = startDate;
      if (endDate) p.end_date = endDate;
    } else { p.days = days; }
    // Multi-account selection has priority; only Meta accounts qualify (Google ad-group not yet implemented)
    const allowedIds = (accountIds && accountIds.length > 0) ? accountIds : null;
    const acctsForQuery = accounts.filter((a) => {
      if (a.network !== "meta") return false;
      if (allowedIds && !allowedIds.includes(a.account_id)) return false;
      if (!allowedIds && accountId && accountId !== "all" && a.account_id !== accountId) return false;
      return true;
    });
    if (acctsForQuery.length > 0) p.account_ids = acctsForQuery.map((a) => a.account_id).join(",");
    return p;
  }, [accounts, accountId, accountIds, days, startDate, endDate, minSpend]);

  // This view is Meta-only (Google ad-group data isn't wired up yet). Decide
  // whether the current network/account selection actually resolves to at least
  // one Meta account. If not, we must NOT fetch — otherwise the backend, given
  // no account_ids, returns EVERY Meta ad set (e.g. showing Singapore when the
  // user picked Google + UK).
  const metaApplicable = useMemo(() => {
    if (network === "google") return false;
    const metaAccts = accounts.filter((a) => a.network === "meta");
    if (!metaAccts.length) return false;
    const allowedIds = (accountIds && accountIds.length > 0) ? accountIds : null;
    if (allowedIds) return metaAccts.some((a) => allowedIds.includes(a.account_id));
    if (accountId && accountId !== "all") return metaAccts.some((a) => a.account_id === accountId);
    return true; // no account restriction → all Meta accounts
  }, [accounts, network, accountId, accountIds]);

  const loadTokenRef = React.useRef(0);
  const loadAbortRef = React.useRef(null);

  const load = async () => {
    if (loadAbortRef.current) {
      try { loadAbortRef.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    loadAbortRef.current = ctrl;
    const myToken = ++loadTokenRef.current;
    setLoading(true);
    try {
      const r = await api.get("/adsets", { params, signal: ctrl.signal });
      if (myToken !== loadTokenRef.current) return;
      setAdsets(r.data.adsets || []);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED" || e?.message === "canceled") return;
      if (myToken !== loadTokenRef.current) return;
      toast.error("Failed to load ad sets", { description: e.response?.data?.detail || e.message });
    } finally {
      if (myToken === loadTokenRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!metaApplicable) { setAdsets([]); setLoading(false); return; }
    load();
    /* eslint-disable-next-line */
  }, [JSON.stringify(params), metaApplicable]);

  const filteredSorted = useMemo(() => {
    // Prefer this view's own filter box; fall back to the parent search prop.
    const q = (filter.trim() || (search || "").trim()).toLowerCase();
    let list = adsets;
    if (q) list = list.filter((a) => (a.adset_name || "").toLowerCase().includes(q) || (a.campaign_name || "").toLowerCase().includes(q) || String(a.adset_id || "").toLowerCase().includes(q));
    const arr = [...list];
    arr.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === "cpa" || sortKey === "daily_budget") {
        const an = !(av > 0), bn = !(bv > 0);
        if (an && bn) return 0;
        if (an) return 1;
        if (bn) return -1;
      }
      if (typeof av === "string" || typeof bv === "string") {
        av = (av || "").toString().toLowerCase();
        bv = (bv || "").toString().toLowerCase();
        if (av < bv) return sortDir === "asc" ? -1 : 1;
        if (av > bv) return sortDir === "asc" ? 1 : -1;
        return 0;
      }
      return sortDir === "asc" ? Number(av || 0) - Number(bv || 0) : Number(bv || 0) - Number(av || 0);
    });
    return arr;
  }, [adsets, sortKey, sortDir, search, filter]);

  // Column totals for the currently filtered/sorted ad sets. Counts summed;
  // cost-per columns blended (total spend ÷ total of that metric).
  const totals = useMemo(() => {
    const t = { count: filteredSorted.length, daily_budget: 0, spend: 0, plat: 0, tm: 0 };
    for (const a of filteredSorted) {
      t.daily_budget += Number(a.daily_budget || 0);
      t.spend += Number(a.spend || 0);
      t.plat += Number(a.platform_registrations || 0);
      t.tm += Number(a.tm_registrations || 0);
    }
    return t;
  }, [filteredSorted]);

  const handleSort = (key) => {
    if (key === sortKey) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "adset_name" || key === "campaign_name" ? "asc" : "desc"); }
  };

  const avgCpa = useMemo(() => {
    const pos = adsets.filter((a) => a.cpa > 0);
    if (!pos.length) return 0;
    return pos.reduce((s, a) => s + a.cpa, 0) / pos.length;
  }, [adsets]);

  const sortIcon = (key) => key !== sortKey ? <ArrowUpDown className="w-3 h-3 inline opacity-30" />
    : sortDir === "asc" ? <ArrowUp className="w-3 h-3 inline" /> : <ArrowDown className="w-3 h-3 inline" />;

  const quick = async (adset, direction, pct = 20) => {
    setBusyId(`${adset.adset_id}-${direction}-${pct}`);
    try {
      const r = await api.post(`/adsets/${adset.adset_id}/quick-budget`, { direction, pct });
      toast.success(
        `${direction === "increase" ? "+" : "−"}${pct}% on ${(adset.adset_name || "").slice(0, 40)}`,
        { description: `£${r.data.current_budget.toFixed(2)} → £${r.data.new_budget.toFixed(2)}` }
      );
      // Optimistic update of the changed row
      setAdsets((list) => list.map((a) => (
        a.adset_id === adset.adset_id
          ? {
              ...a,
              daily_budget: r.data.new_budget,
              budget_changed_at: new Date().toISOString(),
              budget_changed_from: r.data.current_budget,
              budget_changed_to: r.data.new_budget,
              budget_change_pct: pct,
              budget_change_direction: direction,
            }
          : a
      )));
    } catch (e) {
      toast.error("Budget update failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusyId(null); }
  };

  const fmtCell = (col, value) => {
    if (value === null || value === undefined) return col.money ? <span className="text-zinc-400">—</span> : "—";
    if (col.money) return fmtMoney(value);
    if (col.num !== undefined) return fmtNum(value, col.num);
    if (col.pct) return `${Number(value).toFixed(2)}%`;
    return value;
  };

  return (
    <div className="bg-white border border-zinc-200">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
        <div>
          <div className="data-label">Ad sets</div>
          <div className="font-mono text-sm text-zinc-600 mt-1">
            {loading ? "Loading…" : `${filteredSorted.length} of ${adsets.length} ad sets`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56 md:w-72">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="adset-view-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter ad sets or campaigns…"
              className="rounded-none border-zinc-300 pl-9 font-mono text-sm"
            />
          </div>
          <Button onClick={load} disabled={loading} variant="outline" size="sm" className="rounded-none">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="adsets-table">
          <thead>
            <tr className="bg-zinc-50">
              {COLUMNS.map((col) => (
                <th key={col.key} data-testid={`th-adset-${col.key}`}
                    onClick={() => col.sortable !== false && handleSort(col.key)}
                    className={`px-5 py-3 data-label select-none ${col.align === "right" ? "text-right" : "text-left"} ${col.sortable !== false ? "cursor-pointer hover:text-zinc-900" : ""}`}>
                  <span className="inline-flex items-center gap-1">{col.label}{col.sortable !== false ? sortIcon(col.key) : null}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSorted.length === 0 && !loading && (
              <tr>
                <td colSpan={COLUMNS.length} className="text-center py-12 text-zinc-500 font-mono text-sm">
                  {!metaApplicable
                    ? "The ad-set / ad-group view currently shows Meta ad sets only. Select the Meta network (or a Meta ad account) to see data here — Google ad groups aren't available in this view yet."
                    : "No ad sets found."}
                </td>
              </tr>
            )}
            {filteredSorted.map((a) => (
              <tr key={a.adset_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`adset-row-${a.adset_id}`}>
                <td className="px-5 py-3 max-w-xs">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div className="font-medium text-zinc-900 truncate" title={a.adset_name}>{a.adset_name}</div>
                    <CopyButton value={a.adset_name} label="Ad set name" testid={`copy-adset-name-${a.adset_id}`} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="text-xs text-zinc-500 font-mono">ID {a.adset_id}</div>
                    <CopyButton value={a.adset_id} label="Ad set ID" testid={`copy-adset-id-${a.adset_id}`} size={10} />
                  </div>
                </td>
                <td className="px-5 py-3 max-w-xs text-zinc-600 truncate">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate" title={a.campaign_name || ""}>{a.campaign_name || "—"}</span>
                    {a.campaign_name ? (
                      <CopyButton value={a.campaign_name} label="Campaign name" testid={`copy-camp-name-${a.adset_id}`} size={10} />
                    ) : null}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-1 text-xs font-mono border ${
                    (a.status || "").toUpperCase() === "PAUSED"
                      ? "bg-zinc-100 text-zinc-600 border-zinc-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  }`}>{a.status || "—"}</span>
                </td>
                <td className="px-5 py-3 text-right font-mono">
                  <div className="inline-flex items-center gap-1 justify-end">
                    <span>{fmtCell({ money: true }, a.daily_budget)}</span>
                    <RecentBudgetBadge
                      changedAt={a.budget_changed_at}
                      from={a.budget_changed_from}
                      to={a.budget_changed_to}
                      pct={a.budget_change_pct}
                      direction={a.budget_change_direction}
                      testid={`recent-budget-adset-${a.adset_id}`}
                    />
                  </div>
                </td>
                <td className="px-5 py-3 text-right font-mono">{fmtMoney(a.spend)}</td>
                <td className="px-5 py-3 text-right font-mono">{fmtNum(a.ctr, 2)}%</td>
                <td className="px-5 py-3 text-right font-mono">{fmtNum(a.platform_registrations, 1)}</td>
                <td className="px-5 py-3 text-right font-mono">
                  {a.platform_cpa > 0 ? (
                    <span className={`px-1.5 py-0.5 ${
                      avgCpa && a.platform_cpa < avgCpa ? "bg-emerald-50 text-emerald-700"
                      : avgCpa && a.platform_cpa > avgCpa * 1.5 ? "bg-red-50 text-red-700"
                      : ""
                    }`}>{fmtMoney(a.platform_cpa)}</span>
                  ) : "—"}
                </td>
                <td className="px-5 py-3 text-right font-mono">{fmtNum(a.tm_registrations, 1)}</td>
                <td className="px-5 py-3 text-right font-mono">
                  {a.tm_cpa > 0 ? (
                    <span className={`px-1.5 py-0.5 ${
                      avgCpa && a.tm_cpa < avgCpa ? "bg-emerald-50 text-emerald-700"
                      : avgCpa && a.tm_cpa > avgCpa * 1.5 ? "bg-red-50 text-red-700"
                      : ""
                    }`}>{fmtMoney(a.tm_cpa)}</span>
                  ) : "—"}
                </td>
                <td className={`px-5 py-3 text-right font-mono ${
                  a.variance_pct === null || a.variance_pct === undefined ? "text-zinc-400"
                  : Math.abs(a.variance_pct) >= 25 ? "bg-red-50 text-red-700"
                  : Math.abs(a.variance_pct) >= 10 ? "bg-amber-50 text-amber-700"
                  : "text-zinc-600"
                }`} title={`TM ${a.tm_registrations} vs Plat ${a.platform_registrations}`}>
                  {a.variance_pct === null || a.variance_pct === undefined
                    ? "—"
                    : `${a.variance_pct > 0 ? "+" : ""}${Math.round(a.variance_pct)}%`}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  {a.daily_budget != null ? (
                    <BudgetQuickPicker
                      testid={`adset-budget-${a.adset_id}`}
                      currentBudget={a.daily_budget}
                      busy={busyId?.startsWith(`${a.adset_id}-`)}
                      defaultDirection={
                        a.cpa > avgCpa * 1.5 && a.cpa > 0 ? "decrease" : "increase"
                      }
                      onApply={({ direction, pct }) => quick(a, direction, pct)}
                    />
                  ) : (
                    <Link
                      to={`/?campaign=${encodeURIComponent(a.campaign_name || "")}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`adset-open-campaign-${a.adset_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                      title={`Budget set at ${a.budget_level || 'campaign'} level — open campaign`}>
                      <ExternalLink className="w-3 h-3" />
                      Campaign
                    </Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {filteredSorted.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold text-zinc-900" data-testid="adsets-total-row">
                <td className="px-5 py-3 data-label whitespace-nowrap">TOTAL · {totals.count} ad set{totals.count === 1 ? "" : "s"}</td>
                <td className="px-5 py-3"></td>
                <td className="px-5 py-3"></td>
                <td className="px-5 py-3 text-right font-mono">{totals.daily_budget > 0 ? fmtMoney(totals.daily_budget) : "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{fmtMoney(totals.spend)}</td>
                <td className="px-5 py-3 text-right font-mono">—</td>
                <td className="px-5 py-3 text-right font-mono">{fmtNum(totals.plat, 1)}</td>
                <td className="px-5 py-3 text-right font-mono">{totals.plat > 0 ? fmtMoney(totals.spend / totals.plat) : "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{fmtNum(totals.tm, 1)}</td>
                <td className="px-5 py-3 text-right font-mono">{totals.tm > 0 ? fmtMoney(totals.spend / totals.tm) : "—"}</td>
                <td className="px-5 py-3 text-right font-mono">{totals.plat > 0 ? `${totals.tm - totals.plat > 0 ? "+" : ""}${Math.round(((totals.tm - totals.plat) / totals.plat) * 100)}%` : "—"}</td>
                <td className="px-5 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
